import type { ElectiveGroup, Module } from "@/types";

export interface ElectiveOptionSelection {
  optionModules: Module[];
  selectedModules: Module[];
  configuredSelectionCount?: number;
  requiredModuleCount: number;
  requiresModuleChoice: boolean;
  selectionCountValid: boolean;
  selectionComplete: boolean;
  credits: number;
}

/**
 * Resolves the modules and credits represented by one elective option.
 * An option without a choose-N rule requires every assigned module.
 */
export function getElectiveOptionSelection(
  group: ElectiveGroup,
  modules: Module[],
): ElectiveOptionSelection {
  const moduleIds = new Set(group.moduleIds ?? []);
  const optionModules = modules.filter((module) => moduleIds.has(module.id));
  const configuredSelectionCount =
    typeof group.selectionCount === "number" && group.selectionCount > 0
      ? group.selectionCount
      : undefined;
  const selectionCountValid =
    configuredSelectionCount === undefined ||
    (Number.isInteger(configuredSelectionCount) &&
      configuredSelectionCount <= optionModules.length);
  const requiresModuleChoice =
    configuredSelectionCount !== undefined &&
    selectionCountValid &&
    configuredSelectionCount < optionModules.length;
  const requiredModuleCount = requiresModuleChoice
    ? configuredSelectionCount
    : optionModules.length;
  const selectedIds = new Set(group.selectedModuleIds ?? []);
  const selectedModules = requiresModuleChoice
    ? optionModules.filter((module) => selectedIds.has(module.id))
    : optionModules;
  const selectionComplete =
    selectionCountValid &&
    (!requiresModuleChoice || selectedModules.length === requiredModuleCount);
  const credits = selectedModules.reduce((sum, module) => sum + Number(module.credits ?? 0), 0);

  return {
    optionModules,
    selectedModules,
    configuredSelectionCount,
    requiredModuleCount,
    requiresModuleChoice,
    selectionCountValid,
    selectionComplete,
    credits,
  };
}
